import React, { useCallback, createContext, useContext, useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import centroid from '@turf/centroid';

import { getComp, getFkd } from './db';
import { AppContext } from './AppContext';
import { getSsurgo, updateSoilLayer } from './Soils';
import { innerBoundaryToggle } from './Boundary';
import { getTotalAcres } from './geo';

export const FarmContext = createContext();


export let GEO_URL = 'https://geoserver-sx3ktfshsq-uc.a.run.app';
export let DATA_URL  = 'https://db-sx3ktfshsq-uc.a.run.app';
if (process.env.NODE_ENV === 'development') {
  // GEO_URL = 'http://localhost:8080';
  DATA_URL  = 'http://localhost:5000';
}


const mkFc = features => ({
  type: 'FeatureCollection',
  features
});

export const FarmContextProvider = ({ children, model }) => {
  // TODO replace all !boundary with boundary.features.length === 0
  const [boundary, setBoundary] = useState({type: 'FeatureCollection', features: []});
  const boundaryRef = useRef(boundary);
  const [formData, setFormData] = useState({});
  const { user, drawRef, mapboxRef, ctrlRef, showing, toggleShowing } = useContext(AppContext);
  const { compId, fkdId } = useParams();
  const [ssurgoMapunits, setSsurgoMapunits] = useState();
  const modelRef = useRef(model);

  // because when we change model, it won't update "onContextMenu" until boundary toggle
  useEffect(() => {
    modelRef.current = model;
  }, [model])

  const setAllBoundary = useCallback((fc) => {
      setBoundary(fc);
      console.log('fetched', fc, !mapboxRef.current, fc.features.length === 0, !drawRef.current);
      if (!mapboxRef.current) return null;
      if (fc.features.length === 0) return null;
      mapboxRef.current.flyTo({center: centroid(fc).geometry.coordinates, zoom: 12});
      if (!drawRef.current) return null;
      drawRef.current.set(fc);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // fetch data from db
  useEffect(() => {

    if (compId === 'new' || fkdId === 'new') {
      setFormData({});
      setBoundary({type: 'FeatureCollection', features: []});
      drawRef.current && drawRef.current.set(mkFc([]));
      return null;
    }

    if (compId) {
      getComp(user, compId)
        .then(f => {
          setFormData(f);
          const fc = mkFc(f.boundary_set)
          setAllBoundary(fc);
        })
    }

    if (fkdId) {
      getFkd(user, fkdId)
        .then(f => {
          setFormData(f);
          const fc = mkFc(f.boundary_set)
          setAllBoundary(fc);
        })
    }

    console.log('loading', compId);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compId, fkdId, user, drawRef, mapboxRef]);

  // TODO move this to useSoil?
  // fetch ssurgo used by soil and properties
  useEffect(() => {
      if (boundary.features.length === 0) return;
      if (getTotalAcres(boundary) > 10000) return;
      console.log('getting soil')
      getSsurgo(boundary)
        .then(r => setSsurgoMapunits(r))
  }, [boundary]);

  useEffect(() => {
    updateSoilLayer(mapboxRef, ssurgoMapunits, showing['Soils']);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ssurgoMapunits]);

  // the combo of these two is the only way I know of making sure an up to date value of boundary == boundaryRef.current gets used by innerBoundaryToggle when called 
  useEffect(() => {
    boundaryRef.current = boundary;
  }, [boundary]);

  const boundaryToggle = useCallback((toggle) => {
    innerBoundaryToggle({ toggle, mapboxRef, drawRef, boundaryRef, setBoundary, ctrlRef, modelRef });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  const onLoad = useCallback(() => {
    // initial load
    boundaryToggle(true);
    toggleShowing('Boundary', true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundary])



	return <FarmContext.Provider value={{
		boundary, setBoundary,
    formData, setFormData,    
    onLoad,
    compId,
    fkdId,
    ssurgoMapunits,
    boundaryToggle,
    model,
    modelRef
	}} >
		{children}
	</FarmContext.Provider>
}

